import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { combineQueryResults } from '../../../../../../CommonUtils/combineQueryResults';
import {
  ContributionTypesOptionsDTO,
  ContributionYearsOptionsDTO,
  FundingFrequencyTypesOptionsDTO,
  useAchFundingOptionsQuery,
} from '../../../../api/useAchFundingOptionsQuery';
import { StatusDTO, useAchRelationshipsQuery } from '../../../../api/useAchRelationshipsQuery';
import { DepositParams } from '../../../../api/useDepositMutation';
import { useValidateDepositMutation } from '../../../../api/useValidateAccountDepositMutation';
import { useTransferMoneyContext } from '../../../../hooks/useTransferMoneyContext';
import { FormValues } from './DepositForm.types';
import { depositFormValidation } from './DepositForm.validation';

export interface DepositMetadata {
  sourceAccount: string;
  destinationAccount: string;
  note: string;
  scheduledFundingFrequencyLabel: string;
}

interface GetContributionOptionsParams {
  contributionYears: ContributionYearsOptionsDTO;
  isIraAccount: boolean;
}

const getContributionYearsOptions = ({
  isIraAccount,
  contributionYears,
}: GetContributionOptionsParams): ContributionYearsOptions[] | null => {
  if (!isIraAccount) {
    return null;
  }

  return contributionYears.map(year => ({
    label: year.toString(),
    value: year,
  }));
};

export interface ContributionYearsOptions {
  label: string;
  value: number;
}

interface AsyncData {
  accountOptions: OptionType<string>[];
  contributionTypesOptions: ContributionTypesOptionsDTO[] | null;
  contributionYearsOptions: ContributionYearsOptions[] | null;
  isIraAccount: boolean;
  fundingFrequencyTypesOptions: FundingFrequencyTypesOptionsDTO[];
}

interface UseDepositFormParams {
  onSuccess({ params, metadata }: { params: DepositParams; metadata: DepositMetadata }): void;
}

const todayDate = new Date();

export const useDepositForm = ({ onSuccess }: UseDepositFormParams = { onSuccess: () => {} }) => {
  const [depositImpediment, setDepositImpediment] = useState<{
    level: 'error' | 'warning';
    message: string;
  } | null>(null);

  const relationshipsQueryInfo = useAchRelationshipsQuery();

  const fundingOptionsQueryInfo = useAchFundingOptionsQuery();

  const { clientId, managedAccountId, accountName: sourceAccountName } = useTransferMoneyContext();

  const formBag = useForm<FormValues>({
    defaultValues: {
      account: null,
      amount: '',
      contributionTypesOptions: null,
      contributionYearsOptions: null,
      scheduledFundingFrequency: null,
      scheduledFirstDepositDate: todayDate,
      scheduledSecondDepositDate: todayDate,
    },
    context: { isIraAccount: fundingOptionsQueryInfo.data?.isIraAccount },
    resolver: yupResolver(depositFormValidation),
  });

  const depositPreviewMutation = useValidateDepositMutation();

  const currentContributionTypesOptions = formBag.watch('contributionTypesOptions');

  const onSubmit = async submittedData => {
    setDepositImpediment(null);

    const {
      account: { value: fromRelationshipId, label: fromAccountLabel },
      amount,
      contributionTypesOptions,
      contributionYearsOptions,
      scheduledFundingFrequency,
      scheduledFirstDepositDate,
      scheduledSecondDepositDate,
    } = submittedData;

    const params = {
      amount,
      clientId,
      fromRelationshipId,
      contributionYear: contributionYearsOptions?.value,
      toManagedAccountId: managedAccountId,
      iraContributionType: contributionTypesOptions ? contributionTypesOptions.value : null,
      scheduledFundingFrequency: scheduledFundingFrequency.value,
      scheduledFirstDepositDate: moment(scheduledFirstDepositDate).format('YYYY-MM-DD'),
      scheduledSecondDepositDate: scheduledFundingFrequency.showSecondRecurringDepositDay
        ? moment(scheduledSecondDepositDate).format('YYYY-MM-DD')
        : null,
    };

    try {
      const depositValidation = await depositPreviewMutation.mutateAsync(params);

      if (!depositValidation.canProcessTransfer) {
        setDepositImpediment({
          message: depositValidation.rejectReason,
          level: depositValidation.messageLevel.toLowerCase() as 'error' | 'warning',
        });
      } else {
        onSuccess({
          params,
          metadata: {
            note: depositValidation.note,
            sourceAccount: fromAccountLabel,
            destinationAccount: sourceAccountName,
            scheduledFundingFrequencyLabel: scheduledFundingFrequency.label,
          },
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const asyncData: AsyncData | null = useMemo(() => {
    if (!relationshipsQueryInfo.data) {
      return null;
    }

    if (!fundingOptionsQueryInfo.data) {
      return null;
    }

    const accountOptions = relationshipsQueryInfo.data
      .filter(account => account.status === StatusDTO.APPROVED)
      .map(account => ({
        label: account.name,
        value: account.id,
      }));

    const { isIraAccount, contributionTypes, fundingFrequencyTypes } = fundingOptionsQueryInfo.data;

    return {
      accountOptions,
      isIraAccount,
      contributionTypesOptions: contributionTypes,
      contributionYearsOptions: getContributionYearsOptions({
        isIraAccount,
        contributionYears: currentContributionTypesOptions?.contributionYears ?? [],
      }),
      fundingFrequencyTypesOptions: fundingFrequencyTypes,
    };
  }, [relationshipsQueryInfo.data, fundingOptionsQueryInfo.data, currentContributionTypesOptions]);

  return {
    ...combineQueryResults([relationshipsQueryInfo, fundingOptionsQueryInfo]),
    data: asyncData
      ? {
          depositImpediment,
          asyncData,
          formBag,
        }
      : null,
    actions: {
      onSubmit,
    },
  };
};
